import React, { useMemo, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import SearchIcon from '@mui/icons-material/Search';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';
import useFetchDatav2 from '../hooks/useFetchDatav2';
import MainContent from '../components/MainContent';
import Modal from '../components/Modal';
import Spinner from '../components/spinner/Spinner';

function SuperStudents() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [notesInput, setNotesInput] = useState('');
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeReason, setRevokeReason] = useState('');

  const queryKey = useMemo(
    () => ['super-student-whitelist', search, includeRevoked],
    [search, includeRevoked]
  );

  const whitelistURL = useMemo(() => {
    const params = new URLSearchParams();
    params.set('limit', '100');
    if (search) params.set('search', search);
    if (includeRevoked) params.set('include_revoked', 'true');
    return `/super-students/whitelist?${params.toString()}`;
  }, [search, includeRevoked]);

  const { data, isLoading } = useFetchDatav2(
    queryKey,
    whitelistURL,
    {},
    'Could not load whitelist',
    true,
    true
  );

  const studentsListing = useFetchDatav2(
    ['super-students-list', search],
    `/super-students?limit=100${
      search ? `&search=${encodeURIComponent(search)}` : ''
    }`,
    {},
    'Could not load super students',
    true,
    true
  );

  const entries = data?.data?.entries ?? [];
  const bySSId = useMemo(() => {
    const liveSuperStudents = studentsListing.data?.data?.students ?? [];
    const m = new Map();
    liveSuperStudents.forEach((s) => m.set(s.id, s));
    return m;
  }, [studentsListing.data]);

  const addMutation = useMutation({
    mutationFn: async ({ email, notes }) => {
      const res = await axiosInstancev2.post('/super-students/whitelist', {
        email,
        notes,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success('Email whitelisted');
      setEmailInput('');
      setNotesInput('');
      setShowAddModal(false);
      queryClient.invalidateQueries(['super-student-whitelist']);
      queryClient.invalidateQueries(['super-students-list']);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.error || 'Could not whitelist email');
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async ({ id, reason }) => {
      const res = await axiosInstancev2.post(
        `/super-students/whitelist/${id}/revoke`,
        { reason }
      );
      return res.data;
    },
    onSuccess: () => {
      toast.success('Whitelist entry revoked');
      setRevokeTarget(null);
      setRevokeReason('');
      queryClient.invalidateQueries(['super-student-whitelist']);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.error || 'Could not revoke entry');
    },
  });

  const handleAddSubmit = (e) => {
    e.preventDefault();
    if (!emailInput || !emailInput.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    addMutation.mutate({ email: emailInput.trim(), notes: notesInput.trim() });
  };

  const handleRevokeSubmit = (e) => {
    e.preventDefault();
    if (!revokeTarget) return;
    revokeMutation.mutate({ id: revokeTarget.id, reason: revokeReason.trim() });
  };

  return (
    <>
      <MainContent full>
        <div className="flex flex-col gap-4">
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            <NavLink to="/" className="flex items-center gap-1">
              <HomeIcon fontSize="small" /> Home
            </NavLink>
            <span className="text-[#8b173b] font-semibold">Super Students</span>
          </Breadcrumbs>

          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl font-semibold">Super Students</h1>
              <p className="text-sm text-gray-600 mt-1">
                Whitelist an email to grant multi-cohort access. The owner of
                the whitelisted email becomes a Super Student either immediately
                (if they already have an account) or on their next signup.
              </p>
            </div>
            <button
              type="button"
              className="bg-[#8b173b] text-white px-4 py-2 rounded-md flex items-center gap-2 hover:bg-[#701230]"
              onClick={() => setShowAddModal(true)}
            >
              <PersonAddAlt1Icon fontSize="small" />
              Whitelist Email
            </button>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 border rounded-md px-3 py-2 bg-white">
              <SearchIcon fontSize="small" className="text-gray-400" />
              <input
                type="search"
                placeholder="Search by email"
                className="outline-none w-[220px]"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={includeRevoked}
                onChange={(e) => setIncludeRevoked(e.target.checked)}
              />
              Include revoked
            </label>
          </div>

          <div className="bg-white rounded-md shadow-sm overflow-hidden">
            {isLoading && (
              <div className="p-8 flex justify-center">
                <Spinner />
              </div>
            )}
            {!isLoading && entries.length === 0 && (
              <div className="p-10 text-center text-gray-500">
                No whitelist entries yet. Click &quot;Whitelist Email&quot; to
                add one.
              </div>
            )}
            {!isLoading && entries.length > 0 && (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="text-left py-3 px-4">Email</th>
                    <th className="text-left py-3 px-4">Super Student ID</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-left py-3 px-4">Added</th>
                    <th className="text-left py-3 px-4">Notes</th>
                    <th className="py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => {
                    const resolvedStudent = e.resolved_student_id
                      ? bySSId.get(e.resolved_student_id)
                      : null;
                    return (
                      <tr
                        key={e.id}
                        className="border-t hover:bg-gray-50 transition-colors"
                      >
                        <td className="py-3 px-4 font-medium">{e.email}</td>
                        <td className="py-3 px-4 font-mono text-xs">
                          {e.resolved_super_student_id || (
                            <span className="text-gray-400">
                              pending signup
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {e.is_active ? (
                            <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs font-medium">
                              Active
                            </span>
                          ) : (
                            <span className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded text-xs font-medium">
                              Revoked
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          {e.added_at
                            ? new Date(e.added_at).toLocaleDateString('en-us', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })
                            : '—'}
                        </td>
                        <td className="py-3 px-4 text-gray-600 max-w-[240px] truncate">
                          {e.notes || '—'}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-2 justify-end">
                            {resolvedStudent ? (
                              <button
                                type="button"
                                className="text-xs text-[#8b173b] underline"
                                onClick={() =>
                                  navigate(
                                    `/super-students/${resolvedStudent.id}`
                                  )
                                }
                              >
                                View
                              </button>
                            ) : null}
                            {e.is_active ? (
                              <button
                                type="button"
                                className="text-xs text-red-600 flex items-center gap-1"
                                onClick={() => setRevokeTarget(e)}
                              >
                                <DoNotDisturbOnIcon fontSize="inherit" />
                                Revoke
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </MainContent>
      <Modal
        modalOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Whitelist an email"
        small
      >
        <p className="text-xs text-gray-600 mb-4">
          Any account created with this email becomes a Super Student
          automatically. Existing accounts are promoted immediately and receive
          a Super Student announcement email.
        </p>
        <form onSubmit={handleAddSubmit} className="flex flex-col gap-3">
          <div>
            <label
              htmlFor="ss-whitelist-email"
              className="text-xs font-medium text-gray-700 block mb-1"
            >
              Email
            </label>
            <input
              id="ss-whitelist-email"
              type="email"
              required
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              className="border rounded-md px-3 py-2 w-full"
              placeholder="student@example.com"
            />
          </div>
          <div>
            <label
              htmlFor="ss-whitelist-notes"
              className="text-xs font-medium text-gray-700 block mb-1"
            >
              Notes (optional)
            </label>
            <textarea
              id="ss-whitelist-notes"
              value={notesInput}
              onChange={(e) => setNotesInput(e.target.value)}
              className="border rounded-md px-3 py-2 w-full"
              rows={3}
              placeholder="Why is this learner a super student?"
            />
          </div>
          <div className="flex gap-2 justify-end mt-2">
            <button
              type="button"
              onClick={() => setShowAddModal(false)}
              className="px-4 py-2 border rounded-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={addMutation.isLoading}
              className="bg-[#8b173b] text-white px-4 py-2 rounded-md disabled:opacity-60"
            >
              {addMutation.isLoading ? 'Adding...' : 'Whitelist'}
            </button>
          </div>
        </form>
      </Modal>
      <Modal
        modalOpen={revokeTarget !== null}
        onClose={() => setRevokeTarget(null)}
        title="Revoke whitelist"
        small
      >
        {revokeTarget && (
          <>
            <p className="text-xs text-gray-600 mb-4">
              Revoking <strong>{revokeTarget.email}</strong> stops future cohort
              assignments. Existing memberships remain active and can be managed
              individually.
            </p>
            <form onSubmit={handleRevokeSubmit} className="flex flex-col gap-3">
              <div>
                <label
                  htmlFor="ss-revoke-reason"
                  className="text-xs font-medium text-gray-700 block mb-1"
                >
                  Reason (optional)
                </label>
                <input
                  id="ss-revoke-reason"
                  type="text"
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                  className="border rounded-md px-3 py-2 w-full"
                  placeholder="Why are you revoking?"
                />
              </div>
              <div className="flex gap-2 justify-end mt-2">
                <button
                  type="button"
                  onClick={() => setRevokeTarget(null)}
                  className="px-4 py-2 border rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={revokeMutation.isLoading}
                  className="bg-red-600 text-white px-4 py-2 rounded-md disabled:opacity-60"
                >
                  {revokeMutation.isLoading ? 'Revoking...' : 'Revoke'}
                </button>
              </div>
            </form>
          </>
        )}
      </Modal>
    </>
  );
}

export default SuperStudents;
